<script lang="ts" setup>
import { TRANSACTION_TYPE } from '@@/shared/property/types';
import { getUrlParamsFromCriteria } from '@@/shared/property/url-serp';
import { type PropertySearchCriteria, SEARCH_PROPERTY_TYPES_VALUES } from '@@/shared/property/search-criteria';
import type { IadAppRoute } from '~/application';

const open = defineModel<boolean>({ default: false });

const emit = defineEmits<{
  alert: [PropertySearchCriteria]
}>();

const route = useRoute();
const { propertySearchCriteria } = usePropertySearchCriteria(route);
const { modelValue: updatedCriteria, debouncedModelValue: debouncedPropertySearchCriteria, resetFromSource } = useDebouncedCloneOnce(propertySearchCriteria);

// Updated result count
const { locale } = useI18n();
const updatedUrlParams = computed(() => {
  return getUrlParamsFromCriteria(locale.value, debouncedPropertySearchCriteria.value);
});

watch(open, (isDialogOpen) => {
  if (isDialogOpen) resetFromSource();
});

const updatedSerpSlug = toRef(() => updatedUrlParams.value.serpSlug);
const updatedQuery = toRef(() => updatedUrlParams.value.query);
const { data, isPending } = useSearchProperties(updatedSerpSlug, updatedQuery);
const propertyCount = computed(() => data.value?.totalItems ?? 0);

// Button link
const buttonLink = computed<IadAppRoute<`serp-serpSlug`>>(() => {
  return {
    name: 'serp-serpSlug',
    params: { serpSlug: updatedSerpSlug.value },
    query: { ...updatedQuery.value, page: undefined },
  };
});

function clearAllFilters() {
  updatedCriteria.value = {
    ...updatedCriteria.value,
    locations: [],
    propertyTypes: [],
    priceMax: undefined,
    priceMin: undefined,
    surfaceMax: undefined,
    surfaceMin: undefined,
    epc: [],
    rooms: [],
    features: [],
    propertyListingRef: undefined,
    bedrooms: [],
    groundSurfaceMin: undefined,
    groundSurfaceMax: undefined,
    projectTypes: [],
  };
};

function clearAllSecondaryFilters() {
  updatedCriteria.value = {
    ...updatedCriteria.value,
    features: [],
    epc: [],
    propertyListingRef: undefined,
    bedrooms: [],
    groundSurfaceMin: undefined,
    groundSurfaceMax: undefined,
    projectTypes: [],
  };
};

const { filtersCount, filtersKeys } = usePropertySearchCriteriaCount(debouncedPropertySearchCriteria);
function onApplyFilters() {
  trackEvent({
    event: 'search',
    event_name: 'filter_interaction',
    total_filters: filtersCount.value,
    selected_filter_types: filtersKeys.value,
  });
  open.value = false;
}
function onAlert() {
  emit('alert', updatedCriteria.value);
}
</script>

<template>
  <IadDialog
    v-model="open"
    modal-name="search_filters"
  >
    <template #top-actions>
      <IadBtn
        class="lg:hidden"
        variant="link"
        size="fit"
        @click="clearAllFilters"
      >
        <Icon name="iad-icon:delete" />
        <span v-text="$t('property_serp.filters.clear_all')" />
      </IadBtn>
      <IadBtn
        class="hidden lg:flex"
        variant="link"
        size="fit"
        @click="clearAllSecondaryFilters"
      >
        <Icon name="iad-icon:delete" />
        <span v-text="$t('property_serp.filters.clear_all')" />
      </IadBtn>
    </template>
    <template #top-content>
      <IadTitle
        :text="$t(`property_serp.filters.mobile.title`)"
        variant="large"
      />
    </template>
    <div class="p-l flex flex-col">
      <PropertyDialogFilterSection
        :title="$t(`property_serp.filters.transaction_type`)"
        small-device-only
      >
        <IadRadioGroup v-model="updatedCriteria.transactionType">
          <IadRadio
            :label="$t(`core.navigation.buy`)"
            :value="TRANSACTION_TYPE.SALE"
          />
          <IadRadio
            :label="$t(`core.navigation.rent`)"
            :value="TRANSACTION_TYPE.RENTAL"
          />
        </IadRadioGroup>
      </PropertyDialogFilterSection>
      <PropertyDialogFilterSection
        :title="$t(`property_serp.filters.location`)"
        small-device-only
      >
        <PropertyFilterInputLocation v-model="updatedCriteria.locations" />
        <PropertyFilterInputRadius v-model="updatedCriteria.radius" />
      </PropertyDialogFilterSection>
      <PropertyDialogFilterSection
        :title="$t(`property_serp.filters.price`)"
        small-device-only
      >
        <PropertyFilterInputPrice
          v-model="updatedCriteria"
          variant="dialog"
        />
      </PropertyDialogFilterSection>
      <PropertyDialogFilterSection
        :title="$t(`property_serp.property_type.title`)"
        small-device-only
      >
        <IadChipGroup
          v-model="updatedCriteria.propertyTypes"
        >
          <IadChip
            v-for="propertyTypeValue in SEARCH_PROPERTY_TYPES_VALUES"
            :key="propertyTypeValue"
            :label="$t(`property.types_and_sub_types.${propertyTypeValue}`)"
            :value="propertyTypeValue"
          />
        </IadChipGroup>
      </PropertyDialogFilterSection>
      <PropertyDialogFilterSection
        :title="$t(`property_serp.filters.surface`)"
        small-device-only
      >
        <PropertyFilterInputSurface
          v-model="updatedCriteria"
          variant="dialog"
        />
      </PropertyDialogFilterSection>
      <PropertyDialogFilterSection
        :title="$t(`property_serp.filters.rooms`)"
        small-device-only
      >
        <PropertyFilterInputRoom v-model="updatedCriteria.rooms" />
      </PropertyDialogFilterSection>
      <PropertyDialogFilterSection
        :title="$t(`property_serp.filters.bedrooms`)"
      >
        <PropertyFilterInputBedroom v-model="updatedCriteria.bedrooms" />
      </PropertyDialogFilterSection>
      <PropertyDialogFilterSection
        :title="$t(`property_serp.filters.features`)"
      >
        <PropertyFilterInputFeatures v-model="updatedCriteria.features" />
      </PropertyDialogFilterSection>
      <PropertyDialogFilterSection
        :title="$t(`property_serp.filters.energy_performance`)"
      >
        <PropertyFilterInputEpc v-model="updatedCriteria.epc" />
      </PropertyDialogFilterSection>
      <PropertyDialogFilterSection
        :title="$t(`property_serp.filters.ground_surface`)"
      >
        <PropertyFilterInputGroundSurface v-model="updatedCriteria" />
      </PropertyDialogFilterSection>
      <PropertyDialogFilterSection
        :title="$t(`property_serp.filters.project_type.text`)"
      >
        <PropertyFilterInputProjectType v-model="updatedCriteria.projectTypes" />
      </PropertyDialogFilterSection>
      <PropertyDialogFilterSection
        :title="$t('property_serp.filters.reference')"
      >
        <IadInputText
          v-model="updatedCriteria.propertyListingRef"
          :placeholder="$t('property_serp.filters.reference_placeholder')"
          name="reference"
        />
      </PropertyDialogFilterSection>
    </div>

    <template #actions>
      <PropertyFilterButton
        :open="open"
        :class="{
          'w-full': propertyCount === 0,
        }"
        :property-count
        :to="buttonLink"
        :pending="isPending"
        @update:open="onApplyFilters"
        @alert="onAlert"
      />
    </template>
  </IadDialog>
</template>
